import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from 'reactstrap';

import apiOffers from 'api/offers';
import ISearchedPosition from 'api/interfaces/offers/ISearchedPosition';
import DateInput from 'components/layout/forms/DateInput';
import { formatServerDate } from 'services/dates/serverTime';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import numericStringToFloat from 'services/numbers/numericStringToFloat';
import getOfferStatusLabel from 'services/offers/offerStatusLabel';
import getContributionMarginRatio from 'services/products/calculations/getContributionMarginRatio';
import getContributionMarginPerUnit from 'services/products/calculations/getContributionMarginPerUnit';
import getExpenseTotal from 'services/products/calculations/getExpenseTotal';
import getSellingPricePerUnit from 'services/products/calculations/getSellingPricePerUnit';
import getSellingPriceTotal from 'services/products/calculations/getSellingPriceTotal';
import translations from 'services/translations/translations';
import {
  ADDITIONAL_OFFER,
  OPPORTUNITY,
} from 'services/enums/offers/offerTypes';
import { OPEN, OPPORTUNITY_STATUSES } from 'services/enums/offers/Statuses';
import { NDA_MASKED_VALUE } from 'services/enums/offers/ndaProtections';
import { addMessage } from 'store/messages/actions';

import { getMarginClass } from '../offerProduct/calculationWindow/getMarginRatioClassName';
import DepartmentSelect from '../DepartmentSelect';
import PositionCalculationPreview from './PositionCalculationPreview';

import 'style/layout/modal/ModalConfirm.css';

import 'style/offers/SearchPositionModal.css';

interface IRow {
  // Per unit of the position, e.g. €/m²
  contributionMargin: number;
  contributionMarginRatio: number;
  expense: number;
  offerDate: string;
  position: ISearchedPosition;
  sellingPrice: number;
  // The selling price per unit of the position, e.g. €/m²
  sellingPricePerUnit: number;
}

type SortKey =
  | 'position'
  | 'name'
  | 'quantity'
  | 'sellingPricePerUnit'
  | 'sellingPrice'
  | 'expense'
  | 'contributionMargin'
  | 'contributionMarginRatio'
  | 'target'
  | 'customer'
  | 'organizationUnit'
  | 'type'
  | 'status'
  | 'offeredBy'
  | 'offerDate';

// A column of the results: its text decides its width (see columnWidths)
interface IColumn {
  // At most this many characters wide; longer texts wrap
  cap?: number;
  className?: (row: IRow) => string;
  key: SortKey;
  label: string;
  numeric?: boolean;
  // Kept on one line and cut with … at its width; the whole text in the tooltip
  ellipsis?: boolean;
  // The search under the heading: a text, a choice, or a range of numbers or dates
  filter?: () => JSX.Element;
  // At least this many characters wide, for its search
  minChars?: number;
  // The number of the column for its range search
  value?: (row: IRow) => number;
  // Shown instead of the text, e.g. a badge
  render?: (row: IRow) => JSX.Element;
  text: (row: IRow) => string;
}

// The widths of the columns in characters, and the room of a cell around its text
const MIN_COLUMN_CHARS = 4;
const MAX_COLUMN_CHARS = 24;
const CELL_PADDING_REM = 0.9;

// In the time of the browser: the server sends UTC
const formatDate = (dateTime: string): string => formatServerDate(dateTime);

const getSortValue = (row: IRow, key: SortKey): string | number => {
  switch (key) {
    case 'position':
      return row.position.position ?? '';
    case 'name':
      return row.position.name ?? '';
    case 'quantity':
      return row.position.quantity ?? 0;
    case 'target':
      return row.position.targetName ?? '';
    case 'customer':
      return row.position.customerName ?? '';
    case 'organizationUnit':
      return row.position.organizationUnit ?? '';
    case 'type':
      return row.position.offerType;
    case 'status':
      return row.position.offerStatus;
    case 'offeredBy':
      return row.position.offeredBy ?? '';
    case 'offerDate':
      return row.offerDate;
    default:
      return row[key];
  }
};

/**
 * Searches positions of all earlier offers and copies the content of the chosen position
 * to the offer as a new position. With onSelect the chosen position is given to the
 * caller instead, e.g. to add it as a new alternative in the calculation window. A click on
 * a row (or its preview button) shows the calculation of the position in a floating window
 * first, from where it is copied or the user goes back to the results.
 */
const SearchPositionModal = ({
  isOpen,
  offerId,
  offerProductId,
  onCopied,
  onSelect,
  priceList = false,
  toggle,
  type,
}: {
  isOpen: boolean;
  offerId?: number;
  // The position being calculated: the positions of its offer are found too
  offerProductId?: number;
  onCopied?: () => void;
  onSelect?: (position: ISearchedPosition) => void;
  // The rows of the additional price lists saved on the offers, copied to the price list
  priceList?: boolean;
  toggle: () => void;
  type: string;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation([
    translations.offers,
    translations.products,
    translations.common,
  ]);

  // The searches of the columns sent to the server (all offers are searched): the texts of
  // the product, project and customer, the type, status and department, the offer dates
  const [productSearch, setProductSearch] = useState('');
  const [targetSearch, setTargetSearch] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [offerType, setOfferType] = useState('');
  const [status, setStatus] = useState('');
  // The offers of one department, or of all
  const [departmentId, setDepartmentId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [positions, setPositions] = useState<ISearchedPosition[] | null>(null);
  // A search on its way: the earlier results are shown until it comes
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('offerDate');
  const [sortAscending, setSortAscending] = useState(false);
  const [copyingId, setCopyingId] = useState<number | null>(null);
  // The position whose calculation is shown before it is copied
  const [previewed, setPreviewed] = useState<ISearchedPosition | null>(null);
  // The searches of the found rows: the offerer's text and the ranges of the numbers, by
  // the label of the column
  const [offererSearch, setOffererSearch] = useState('');
  const [ranges, setRanges] = useState<{
    [label: string]: { max: string; min: string };
  }>({});

  // An opportunity has no positions: its type and closing statuses are not searched
  const withoutKeys = (
    options: { [key: string]: string },
    excluded: string[]
  ): { [key: string]: string } =>
    Object.keys(options)
      .filter((key) => !excluded.includes(key))
      .reduce(
        (result, key) => ({ ...result, [key]: options[key] }),
        {} as { [key: string]: string }
      );
  const typeOptions = withoutKeys(
    t(`${translations.offers}:types`, {
      returnObjects: true,
    }) as { [type: string]: string },
    [OPPORTUNITY]
  );
  const statusOptions = withoutKeys(
    t(`${translations.offers}:statuses`, {
      returnObjects: true,
    }) as { [status: string]: string },
    OPPORTUNITY_STATUSES.filter(
      (opportunityStatus) => opportunityStatus !== OPEN
    )
  );

  // A window opened again starts without the results of the last time
  useEffect(() => {
    if (isOpen) {
      setPositions(null);
    }
  }, [isOpen]);

  // Search as the user types or changes a filter, once the typing pauses. The results and
  // the searches of the columns stay on the page meanwhile, so a field being typed in is
  // not taken away from under the user (it was, when the table was hidden for the search)
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let cancelled = false;

    const timeout = setTimeout(async () => {
      setLoading(true);
      try {
        // The offer being filled is searched too, whatever its revision
        const isAdditionalOffer = type === ADDITIONAL_OFFER;
        const found = await apiOffers.positionSearch.get({
          currentAdditionalOfferId: isAdditionalOffer ? offerId : undefined,
          currentOfferId: isAdditionalOffer ? undefined : offerId,
          currentOfferProductId: offerProductId,
          customerSearch: customerSearch.trim() || undefined,
          // The days of the user: from the start of the first, before the day after the last
          offerDateFrom: dateFrom
            ? new Date(`${dateFrom}T00:00:00`).toISOString()
            : undefined,
          offerDateTo: dateTo
            ? new Date(
                new Date(`${dateTo}T00:00:00`).getTime() + 24 * 60 * 60 * 1000
              ).toISOString()
            : undefined,
          organizationUnitId: departmentId ? Number(departmentId) : undefined,
          priceList: priceList || undefined,
          productSearch: productSearch.trim() || undefined,
          status: status || undefined,
          targetSearch: targetSearch.trim() || undefined,
          type: offerType || undefined,
        });
        if (!cancelled) {
          setPositions(found);
        }
      } catch (error) {
        if (!cancelled) {
          setPositions([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [
    isOpen,
    productSearch,
    targetSearch,
    customerSearch,
    offerType,
    status,
    departmentId,
    dateFrom,
    dateTo,
    offerId,
    offerProductId,
    priceList,
    type,
  ]);

  // Economic figures of the primary calculation version of each position
  const rows: IRow[] = useMemo(
    () =>
      (positions ?? []).map((position) => {
        const primaryVersion = position.versions.find(
          (version) => version.isPrimary
        );
        const quantity = position.quantity ?? 0;

        const sellingPrice =
          primaryVersion === undefined
            ? 0
            : getSellingPriceTotal(
                primaryVersion.installations,
                primaryVersion.subproducts,
                quantity
              );
        const expense =
          primaryVersion === undefined
            ? 0
            : getExpenseTotal(
                primaryVersion.installations,
                primaryVersion.subproducts,
                quantity
              );
        // Per unit of the position, e.g. €/m², comparable whatever the quantity
        const contributionMargin =
          primaryVersion === undefined
            ? 0
            : getContributionMarginPerUnit(
                primaryVersion.installations,
                primaryVersion.subproducts
              );

        return {
          contributionMargin,
          // Per unit, so it does not depend on the quantity (as on the offer page)
          contributionMarginRatio:
            primaryVersion === undefined
              ? 0
              : getContributionMarginRatio(
                  primaryVersion.installations,
                  primaryVersion.subproducts
                ),
          expense,
          offerDate: position.offerDate,
          position,
          sellingPrice,
          sellingPricePerUnit:
            primaryVersion === undefined
              ? 0
              : getSellingPricePerUnit(
                  primaryVersion.installations,
                  primaryVersion.subproducts
                ),
        };
      }),
    [positions]
  );

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        // The model positions (mallipohja) first, whatever the order
        if (a.position.isTemplate !== b.position.isTemplate) {
          return a.position.isTemplate ? -1 : 1;
        }
        const first = getSortValue(a, sortKey);
        const second = getSortValue(b, sortKey);
        const result =
          typeof first === 'number' && typeof second === 'number'
            ? first - second
            : String(first).localeCompare(String(second), 'fi', {
                numeric: true,
              });
        return sortAscending ? result : -result;
      }),
    [rows, sortKey, sortAscending]
  );

  const sortBy = (key: SortKey) => {
    if (key === sortKey) {
      setSortAscending(!sortAscending);
    } else {
      setSortKey(key);
      setSortAscending(true);
    }
  };

  const copyLabel =
    onSelect === undefined
      ? t(`${translations.offers}:positionSearch.Copy`)
      : t(`${translations.offers}:positionSearch.Add as alternative`);

  // The search of the positions, of a new alternative or of the price list rows
  const getTitle = () => {
    if (priceList) {
      return t(`${translations.offers}:positionSearch.Title for price list`);
    }

    return onSelect === undefined
      ? t(`${translations.offers}:positionSearch.Title`)
      : t(`${translations.offers}:positionSearch.Title for alternative`);
  };

  const getPreviewCopyLabel = () => {
    if (priceList) {
      return t(`${translations.offers}:positionSearch.Add to price list`);
    }

    return onSelect === undefined
      ? t(`${translations.offers}:positionSearch.Add to offer`)
      : copyLabel;
  };

  const copy = async (position: ISearchedPosition) => {
    if (onSelect !== undefined) {
      onSelect(position);
      setPreviewed(null);
      toggle();
      return;
    }

    if (offerId === undefined) {
      return;
    }

    setCopyingId(position.offerProductId);

    try {
      if (type === ADDITIONAL_OFFER) {
        await apiOffers.additionalOffers.copiedPositions.post(
          offerId,
          position.offerProductId
        );
      } else {
        await apiOffers.copiedPositions.post(offerId, position.offerProductId);
      }

      dispatch(addMessage(t(`${translations.offers}:positionSearch.Copied`)));
      onCopied?.();
      setPreviewed(null);
      toggle();
    } catch (error) {
      dispatch(
        addMessage(t(`${translations.offers}:positionSearch.Copy failed`))
      );
    } finally {
      setCopyingId(null);
    }
  };

  const header = (key: SortKey, text: string, numeric = false) => {
    const sorted = sortKey === key;
    const direction = sortAscending ? 'ascending' : 'descending';
    const arrow = sortAscending ? ' ▲' : ' ▼';

    return (
      <th
        key={key}
        className={`sortable${numeric ? ' numeric' : ''}${
          sorted ? ' sorted' : ''
        }`}
        onClick={() => sortBy(key)}
        aria-sort={sorted ? direction : 'none'}
      >
        {text}
        {sorted ? arrow : ''}
      </th>
    );
  };

  const unitText = (unit: string) => t(`${translations.common}:units:${unit}`);

  // The customer of an NDA offer the user is outside of is not shown
  const customerText = (customerName: string | null) =>
    customerName === NDA_MASKED_VALUE
      ? t(`${translations.offers}:nda.Protected`)
      : customerName ?? '';

  // The label of a column by its key, for the range searches keyed by the label
  const columnLabel = (key: SortKey): string =>
    ({
      contributionMargin: `${t(
        `${translations.offers}:Contribution margin`
      )} € / ${t(`${translations.offers}:positionSearch.unit`)}`,
      contributionMarginRatio: `${t(
        `${translations.offers}:Contribution margin`
      )} %`,
      quantity: t(`${translations.common}:Quantity`),
      sellingPricePerUnit: t(`${translations.offers}:Price per unit`),
    }[key as string] ?? key);

  // A text search of a column
  const textFilter = (
    label: string,
    value: string,
    onChange: (value: string) => void
  ) => (
    <input
      aria-label={`${t(
        `${translations.offers}:positionSearch.Search column`
      )}: ${label}`}
      className="form-control"
      onChange={(event) => onChange(event.target.value)}
      type="search"
      value={value}
    />
  );

  // A choice of a column, all when empty
  const choiceFilter = (
    label: string,
    value: string,
    onChange: (value: string) => void,
    options: { [key: string]: string }
  ) => (
    <select
      aria-label={label}
      className="form-control"
      onChange={(event) => onChange(event.target.value)}
      value={value}
    >
      <option value="">{t(`${translations.offers}:positionSearch.All`)}</option>
      {Object.entries(options).map(([key, text]) => (
        <option key={key} value={key}>
          {text}
        </option>
      ))}
    </select>
  );

  // A range of the numbers of a column (of the found rows): at least and at most
  const rangeFilter = (label: string) => {
    const range = ranges[label] ?? { max: '', min: '' };
    const set = (bound: 'max' | 'min', value: string) =>
      setRanges({ ...ranges, [label]: { ...range, [bound]: value } });
    return (
      <div className="search-position-range">
        {(['min', 'max'] as const).map((bound) => (
          <input
            aria-label={`${label} ${t(
              `${translations.offers}:positionSearch.${bound}`
            )}`}
            className="form-control"
            inputMode="decimal"
            key={bound}
            onChange={(event) => set(bound, event.target.value)}
            placeholder={t(`${translations.offers}:positionSearch.${bound}`)}
            value={range[bound]}
          />
        ))}
      </div>
    );
  };

  const inRange = (label: string, value: number) => {
    const range = ranges[label];
    const min = range?.min.trim() ? numericStringToFloat(range.min) : NaN;
    const max = range?.max.trim() ? numericStringToFloat(range.max) : NaN;
    return (
      (Number.isNaN(min) || value >= min) && (Number.isNaN(max) || value <= max)
    );
  };

  // The columns of the results. Each is as wide as its longest text in the results (in
  // characters), the long texts (product, project, customer) wrapped at a cap, so the
  // short columns take no more room than they need.
  const columns: IColumn[] = [
    {
      cap: 34,
      className: () => 'search-position-name',
      filter: () =>
        textFilter(
          t(`${translations.products}:Product`),
          productSearch,
          setProductSearch
        ),
      key: 'name',
      label: t(`${translations.products}:Product`),
      text: (row) => row.position.name ?? '',
    },
    {
      filter: () => rangeFilter(columnLabel('quantity')),
      key: 'quantity',
      minChars: 7,
      value: (row) => row.position.quantity ?? 0,
      label: t(`${translations.common}:Quantity`),
      numeric: true,
      text: (row) =>
        `${formatGroupedNumber(row.position.quantity ?? 0, 2, 0)} ${unitText(
          row.position.unit
        )}`,
    },
    {
      filter: () => rangeFilter(columnLabel('sellingPricePerUnit')),
      key: 'sellingPricePerUnit',
      minChars: 7,
      value: (row) => row.sellingPricePerUnit,
      label: t(`${translations.offers}:Price per unit`),
      numeric: true,
      text: (row) =>
        `${formatGroupedNumber(row.sellingPricePerUnit)} €/${unitText(
          row.position.unit
        )}`,
    },
    {
      filter: () => rangeFilter(columnLabel('contributionMargin')),
      key: 'contributionMargin',
      minChars: 7,
      value: (row) => row.contributionMargin,
      label: `${t(`${translations.offers}:Contribution margin`)} € / ${t(
        `${translations.offers}:positionSearch.unit`
      )}`,
      numeric: true,
      text: (row) =>
        `${formatGroupedNumber(row.contributionMargin)} €/${unitText(
          row.position.unit
        )}`,
    },
    {
      // Coloured as in the calculation window: low red, high green
      className: (row) =>
        `search-position-margin ${getMarginClass(row.contributionMarginRatio)}`,
      filter: () => rangeFilter(columnLabel('contributionMarginRatio')),
      key: 'contributionMarginRatio',
      minChars: 7,
      value: (row) => row.contributionMarginRatio,
      label: `${t(`${translations.offers}:Contribution margin`)} %`,
      numeric: true,
      text: (row) => `${formatGroupedNumber(row.contributionMarginRatio)} %`,
    },
    {
      cap: 34,
      filter: () =>
        textFilter(
          t(`${translations.offers}:Project`),
          targetSearch,
          setTargetSearch
        ),
      key: 'target',
      label: t(`${translations.offers}:Project`),
      text: (row) =>
        `${row.position.targetName ?? ''}${
          row.position.revisionNumber > 1
            ? ` (v${row.position.revisionNumber})`
            : ''
        }`,
    },
    {
      cap: 26,
      filter: () =>
        textFilter(
          t(`${translations.offers}:Customer Office`),
          customerSearch,
          setCustomerSearch
        ),
      key: 'customer',
      label: t(`${translations.offers}:Customer Office`),
      text: (row) => customerText(row.position.customerName),
    },
    {
      // E.g. "25 Tampere": the number and the start of the name are enough
      cap: 11,
      ellipsis: true,
      filter: () => (
        <DepartmentSelect onChange={setDepartmentId} value={departmentId} />
      ),
      key: 'organizationUnit',
      minChars: 11,
      label: t(`${translations.offers}:positionSearch.Organization unit`),
      text: (row) => row.position.organizationUnit ?? '',
    },
    {
      filter: () =>
        choiceFilter(
          t(`${translations.offers}:Offer Type`),
          offerType,
          setOfferType,
          typeOptions
        ),
      key: 'type',
      label: t(`${translations.offers}:Offer Type`),
      text: (row) =>
        typeOptions[row.position.offerType] ?? row.position.offerType,
    },
    {
      // The start of a long status is enough, e.g. "Lähetysv…"
      cap: 8,
      ellipsis: true,
      filter: () =>
        choiceFilter(
          t(`${translations.offers}:Status`),
          status,
          setStatus,
          statusOptions
        ),
      key: 'status',
      label: t(`${translations.offers}:Status`),
      minChars: 9,
      render: (row) => (
        <span
          className={`search-position-status status-${row.position.offerStatus}`}
        >
          {getOfferStatusLabel(
            t,
            row.position.offerStatus,
            row.position.offerType
          )}
        </span>
      ),
      text: (row) =>
        getOfferStatusLabel(
          t,
          row.position.offerStatus,
          row.position.offerType
        ),
    },
    {
      filter: () =>
        textFilter(
          t(`${translations.offers}:positionSearch.Offered by`),
          offererSearch,
          setOffererSearch
        ),
      key: 'offeredBy',
      label: t(`${translations.offers}:positionSearch.Offered by`),
      text: (row) => row.position.offeredBy ?? '',
    },
    {
      filter: () => (
        <div className="search-position-range">
          <DateInput
            ariaLabel={t(`${translations.offers}:positionSearch.From`)}
            onChange={setDateFrom}
            placeholder={t(`${translations.offers}:positionSearch.From`)}
            title={t(`${translations.offers}:positionSearch.From`)}
            value={dateFrom}
          />
          {/* Opens a month after the from */}
          <DateInput
            ariaLabel={t(`${translations.offers}:positionSearch.To`)}
            onChange={setDateTo}
            placeholder={t(`${translations.offers}:positionSearch.To`)}
            rangeStart={dateFrom}
            title={t(`${translations.offers}:positionSearch.To`)}
            value={dateTo}
          />
        </div>
      ),
      key: 'offerDate',
      label: t(`${translations.offers}:positionSearch.Offer date`),
      minChars: 13,
      text: (row) => formatDate(row.offerDate),
    },
  ];

  // In characters: the longest text of the column (at least the longest word of its
  // heading, which wraps), at most its cap
  const columnWidths = columns.map((column) => {
    const longest = Math.max(
      0,
      ...sortedRows.map((row) => column.text(row).length)
    );
    const headingWord = Math.max(
      ...column.label.split(/\s+/).map((word) => word.length + 2)
    );
    return Math.min(
      Math.max(longest, headingWord, column.minChars ?? 0, MIN_COLUMN_CHARS),
      column.cap ?? MAX_COLUMN_CHARS
    );
  });

  // The found rows within the ranges of the numbers and with the offerer searched; the
  // widths stay those of all the found rows, so the table does not jump while typing
  const offerer = offererSearch.trim().toLocaleLowerCase();
  const shownRows = sortedRows.filter(
    (row) =>
      columns.every(
        (column) =>
          column.value === undefined || inRange(column.label, column.value(row))
      ) &&
      (offerer === '' ||
        (row.position.offeredBy ?? '').toLocaleLowerCase().includes(offerer))
  );

  const searching = [
    productSearch,
    targetSearch,
    customerSearch,
    offerType,
    status,
    departmentId,
    dateFrom,
    dateTo,
    offererSearch,
    ...Object.values(ranges).flatMap((range) => [range.min, range.max]),
  ].some((value) => value.trim() !== '');

  const clearSearches = () => {
    setProductSearch('');
    setTargetSearch('');
    setCustomerSearch('');
    setOfferType('');
    setStatus('');
    setDepartmentId('');
    setDateFrom('');
    setDateTo('');
    setOffererSearch('');
    setRanges({});
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      className="search-position-modal"
    >
      <ModalHeader className="modal-header" toggle={toggle}>
        {getTitle()}
      </ModalHeader>
      <ModalBody>
        {positions === null ? (
          <p className="search-position-info">
            {t(`${translations.offers}:positionSearch.Searching`)}
          </p>
        ) : (
          <p className="search-position-info">
            {loading
              ? t(`${translations.offers}:positionSearch.Searching`)
              : t(`${translations.offers}:positionSearch.Results`, {
                  count: positions.length,
                })}
            {positions.length >= 200
              ? ` ${t(`${translations.offers}:positionSearch.Limited`)}`
              : ''}
            {shownRows.length < sortedRows.length
              ? ` ${t(
                  `${translations.offers}:positionSearch.Shown by columns`,
                  {
                    count: shownRows.length,
                  }
                )}`
              : ''}
            {searching ? (
              <>
                {' '}
                <button
                  className="search-position-clear"
                  onClick={clearSearches}
                  type="button"
                >
                  {t(
                    `${translations.offers}:positionSearch.Clear column searches`
                  )}
                </button>
              </>
            ) : null}
          </p>
        )}

        {positions !== null ? (
          <div
            className={`search-position-table ${
              loading ? 'search-position-loading' : ''
            }`}
          >
            <Table
              hover
              size="sm"
              style={{
                tableLayout: 'fixed',
                // The whole width of the window: the room left over is shared by the
                // columns in the proportion of their texts
                minWidth: '100%',
                width: `calc(${columnWidths.reduce(
                  (sum, width) => sum + width,
                  0
                )}ch + ${columns.length * CELL_PADDING_REM}rem)`,
              }}
            >
              {/* Each column as wide as its longest text, the long texts wrapped at a cap */}
              <colgroup>
                {columns.map((column, index) => (
                  <col
                    key={column.label}
                    style={{
                      width: `calc(${columnWidths[index]}ch + ${CELL_PADDING_REM}rem)`,
                    }}
                  />
                ))}
              </colgroup>
              <thead>
                <tr>
                  {columns.map((column) =>
                    header(column.key, column.label, column.numeric)
                  )}
                </tr>
                {/* The search of each column, by its kind */}
                <tr className="search-position-column-searches">
                  {columns.map((column) => (
                    <th key={column.label}>{column.filter?.() ?? null}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shownRows.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length}>
                      {t(
                        `${translations.offers}:positionSearch.No column matches`
                      )}
                    </td>
                  </tr>
                ) : null}
                {shownRows.map((row) => (
                  <tr
                    // A model position (mallipohja) on light lime
                    className={`search-position-row${
                      row.position.isTemplate ? ' search-position-template' : ''
                    }`}
                    key={row.position.offerProductId}
                    // A click on the row shows the calculation of the position
                    onClick={() => setPreviewed(row.position)}
                    title={t(
                      `${translations.offers}:positionSearch.Preview help`
                    )}
                  >
                    {columns.map((column) => (
                      <td
                        className={`${column.numeric ? 'numeric' : ''} ${
                          column.ellipsis ? 'search-position-ellipsis' : ''
                        } ${column.className?.(row) ?? ''}`}
                        key={column.label}
                        title={column.ellipsis ? column.text(row) : undefined}
                      >
                        {column.render?.(row) ?? column.text(row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : null}

        {/* The calculation of the clicked position, floating over the results. Drawn on
            the page itself: inside the search window its width would be that of the window */}
        {previewed === null
          ? null
          : createPortal(
              <div className="position-preview-backdrop">
                <PositionCalculationPreview
                  copyLabel={getPreviewCopyLabel()}
                  copying={copyingId !== null}
                  onBack={() => setPreviewed(null)}
                  onCopy={() => copy(previewed)}
                  position={previewed}
                />
              </div>,
              document.body
            )}
      </ModalBody>
      <ModalFooter>
        <Button className="modal-button" onClick={toggle}>
          {t(`${translations.offers}:Close`)}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SearchPositionModal;
